import { SwitchStatus } from '../../entities/switchStatus'
import { TagId } from '../../entities/tagId'
import {
  AesStandDataResult,
  convertStringToStandResponseResult,
} from './aesTypes'

export type AesStandData = {
  commandCode: string
  standResult: AesStandDataResult
  switchStatus: SwitchStatus
  readTimes: number
  firstReaderTagId: TagId | null
  secondReaderTagId: TagId | null
  selectedTagId: TagId | null
  isPreClosed: boolean
  firmwareVersion: string
  rawData: string[]
}

function toHex2(byte: number): string {
  return byte.toString(16).padStart(2, '0')
}

export function convertStringToSwitchStatus(raw: string): SwitchStatus {
  switch (raw) {
    case '00':
      return 'notPressedBoth'
    case '01':
      return 'pressedFirst'
    case '02':
      return 'pressedSecond'
    case '03':
      return 'pressedBoth'
    default:
      return 'unknown'
  }
}

function readTimesOfResult(standResult: AesStandDataResult): number {
  switch (standResult) {
    case 'timeouted':
      return 0
    case 'readFirstReaderOnly':
      return 1
    case 'readSecondReaderOnly':
      return 1
    case 'success':
      return 2
    case 'preClosedReadBothReaderFailed':
      return 0
    case 'preClosedReadFirstReaderOnly':
      return 1
    case 'irregular':
      return 99
  }
}

function checkIfStandIsPreClosed(rawData: string[]): boolean {
  if (rawData.length === 32) {
    return rawData[26] === '01'
  }
  return false
}

function getFirmwareVersion(rawData: string[]): string {
  if (rawData.length === 32) {
    if (rawData[29] === 'c4') {
      return rawData[25]
    }
  }
  return '00'
}

function checkIfHardwareIsPreUpdate(
  rawData: string[],
  canDistinguishReaders: boolean
): boolean {
  if (canDistinguishReaders && rawData.length === 16) return true
  if (canDistinguishReaders && rawData.length === 31) return true
  return false
}

// 読み取り（タグ抽出）を主張する結果は、その応答形式の正規フレーム長でなければ
// malformed とみなす。短い/欠損した応答で空の TagId を success 扱いしないための防御。
// modern: result frame は 32byte。legacy: 4byte card=16byte / 7byte card=32byte。
function hasValidLengthForReads(
  rawData: string[],
  canDistinguishReaders: boolean
): boolean {
  if (canDistinguishReaders) {
    return rawData.length === 32
  }
  return rawData.length === 16 || rawData.length === 32
}

function selectMaybeWentThroughTagId(
  canDistinguishReaders: boolean,
  standResult: AesStandDataResult,
  first: TagId | null,
  second: TagId | null
): TagId | null {
  // platform `maybeWentThroughTagId` を fall-through 含めて 1:1 移植
  if (!canDistinguishReaders) {
    if (standResult === 'readFirstReaderOnly') {
      if (first !== null) return first
      if (second !== null) return second
    }
    if (first !== null) return second
    if (second !== null) return first
  } // 両タグ null 時は early-return せず下の common ブロックへ fall-through（platform 同一・load-bearing。early-return を足さないこと）

  if (
    standResult === 'readFirstReaderOnly' ||
    standResult === 'preClosedReadFirstReaderOnly'
  ) {
    return first
  }
  if (standResult === 'readSecondReaderOnly') {
    return second
  }

  if (first !== null) return second
  if (second !== null) return first

  return null
}

export function parseAesStandData(
  responseBytes: number[],
  canDistinguishReaders: boolean
): AesStandData {
  const rawData = responseBytes.map(toHex2)

  const commandCode = rawData[0]
  const standResult = convertStringToStandResponseResult(
    canDistinguishReaders ? rawData[6] : rawData[2]
  )
  let switchStatus = convertStringToSwitchStatus(
    canDistinguishReaders ? rawData[7] : rawData[3]
  )
  const readTimes = readTimesOfResult(standResult)
  const isPreClosed = checkIfStandIsPreClosed(rawData)
  const firmwareVersion = getFirmwareVersion(rawData)

  // 防御: 読み取り（success/partial）を主張する結果コードなのに応答長が正規フレーム長で
  // ない場合、欠損データから空の TagId を success 扱いしてしまう。malformed として irregular
  // に倒す（BLE/暗号境界の防御。frame の header/length 検証は消費側 isWellFormedAesFrame で実施済。crc 検証は TODO(aikasa/ikasa_platform#7595)＝実フレームが流れる live decode 時）。
  if (
    (readTimes === 1 || readTimes === 2) &&
    !hasValidLengthForReads(rawData, canDistinguishReaders)
  ) {
    return {
      commandCode,
      standResult: 'irregular',
      switchStatus: 'unknown',
      readTimes: readTimesOfResult('irregular'),
      firstReaderTagId: null,
      secondReaderTagId: null,
      selectedTagId: null,
      isPreClosed,
      firmwareVersion,
      rawData,
    }
  }

  const getFirstFoundTagId = (): TagId =>
    new TagId(
      canDistinguishReaders
        ? rawData.slice(8, 15).join('')
        : rawData.length === 32
        ? rawData.slice(4, 11).join('')
        : rawData.slice(4, 8).join('')
    )
  const getSecondFoundTagId = (): TagId =>
    new TagId(
      canDistinguishReaders
        ? rawData.slice(15, 22).join('')
        : rawData.length === 32
        ? rawData.slice(11, 18).join('')
        : rawData.slice(8, 12).join('')
    )

  let firstReaderTagId: TagId | null = null
  let secondReaderTagId: TagId | null = null

  switch (readTimes) {
    case 0:
      firstReaderTagId = null
      secondReaderTagId = null
      // アプデ前はスイッチ値が異常値なので unknown にして使わせない
      if (checkIfHardwareIsPreUpdate(rawData, canDistinguishReaders)) {
        switchStatus = 'unknown'
      }
      break
    case 1:
      // 通常解錠で「2つ目のみ」は legacy では起こり得ない
      if (!canDistinguishReaders) {
        firstReaderTagId = getFirstFoundTagId()
        secondReaderTagId = null
        break
      }
      if (standResult === 'readFirstReaderOnly') {
        firstReaderTagId = getFirstFoundTagId()
        secondReaderTagId = null
        break
      }
      if (standResult === 'readSecondReaderOnly') {
        firstReaderTagId = null
        secondReaderTagId = getSecondFoundTagId()
        break
      }
      if (standResult === 'preClosedReadFirstReaderOnly') {
        firstReaderTagId = getFirstFoundTagId()
        secondReaderTagId = null
        break
      }
      break
    case 2:
      firstReaderTagId = getFirstFoundTagId()
      secondReaderTagId = getSecondFoundTagId()
      break
    default:
      firstReaderTagId = null
      secondReaderTagId = null
  }

  const selectedTagId = selectMaybeWentThroughTagId(
    canDistinguishReaders,
    standResult,
    firstReaderTagId,
    secondReaderTagId
  )

  return {
    commandCode,
    standResult,
    switchStatus,
    readTimes,
    firstReaderTagId,
    secondReaderTagId,
    selectedTagId,
    isPreClosed,
    firmwareVersion,
    rawData,
  }
}
